import { useNavigate } from 'react-router-dom'
import { getClientLoginPath, getClientPublicEntryPath } from '@/config/tenant'
import { clearGuestSession } from '@/utils/guestAuth'
import styles from './GuestClientPanel.module.css'

export type GuestClientPanelVariant = 'perfil' | 'historico' | 'notificacoes' | 'generic'

const COPY: Record<
  GuestClientPanelVariant,
  { icon: string; title: string; description: string; bullets: string[] }
> = {
  perfil: {
    icon: '👤',
    title: 'Está a explorar como visitante',
    description:
      'Nesta sessão ainda não tem uma conta ligada. Pode navegar pela app e marcar horários — ao confirmar, pedimos apenas contacto para a marcação.',
    bullets: [
      'Ver e editar os seus dados',
      'Consultar histórico de marcações',
      'Receber lembretes e notificações',
    ],
  },
  historico: {
    icon: '📜',
    title: 'Histórico só com conta',
    description:
      'Em modo visitante não é possível ver marcações anteriores. Se já marcou, use o e-mail da confirmação para iniciar sessão.',
    bullets: [
      'Marcações futuras e passadas',
      'Remarcar ou cancelar horários',
      'Pacotes e compras associadas',
    ],
  },
  notificacoes: {
    icon: '🔔',
    title: 'Notificações com sessão iniciada',
    description:
      'Lembretes e avisos são enviados à sua conta. Inicie sessão com o e-mail usado na marcação para os ver aqui.',
    bullets: ['Lembretes antes do atendimento', 'Confirmações e alterações', 'Avisos da empresa'],
  },
  generic: {
    icon: '✨',
    title: 'Modo visitante',
    description: 'Explore a app livremente. Para funcionalidades pessoais, inicie sessão como cliente.',
    bullets: ['Perfil e dados', 'Histórico', 'Notificações'],
  },
}

export interface GuestClientPanelProps {
  variant?: GuestClientPanelVariant
  showAgendar?: boolean
  className?: string
}

export function GuestClientPanel({
  variant = 'generic',
  showAgendar = true,
  className,
}: GuestClientPanelProps) {
  const navigate = useNavigate()
  const copy = COPY[variant]

  const handleLogin = () => {
    navigate(getClientLoginPath())
  }

  const handleAgendar = () => {
    navigate('/agendamento')
  }

  const handleEndVisit = () => {
    clearGuestSession()
    window.location.replace(getClientPublicEntryPath())
  }

  return (
    <div className={`${styles.panel} ${className ?? ''}`.trim()} role="status">
      <span className={styles.icon} aria-hidden>
        {copy.icon}
      </span>
      <p className={styles.badge}>Modo visitante</p>
      <h2 className={styles.title}>{copy.title}</h2>
      <p className={styles.description}>{copy.description}</p>

      <div className={styles.bullets}>
        <p className={styles.bulletsTitle}>Com conta de cliente pode:</p>
        <ul>
          {copy.bullets.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </div>

      <div className={styles.actions}>
        <button type="button" className={styles.btnPrimary} onClick={handleLogin}>
          Iniciar sessão
        </button>
        {showAgendar && (
          <button type="button" className={styles.btnSecondary} onClick={handleAgendar}>
            Agendar horário
          </button>
        )}
        <button type="button" className={styles.btnGhost} onClick={handleEndVisit}>
          Terminar visita
        </button>
      </div>
    </div>
  )
}
